import { Box, Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

/**
 * Asks before something destructive or irreversible happens. `description` is where the
 * consequence goes — what will be lost, and who else it reaches.
 */
type Props = {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  extra?: ReactNode;
  error?: { message: string } | null;
  isLoading: boolean;
};

export function ConfirmModal({
  opened,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  extra,
  error,
  isLoading,
}: Props) {
  return (
    <Modal opened={opened} onClose={onClose} size={389}>
      <Stack gap={0}>
        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="black">
          {title}
        </Text>
        <Text size="md" fw={FONT_WEIGHTS.medium} c="black" mt={8}>
          {description}
        </Text>
        {error && (
          <Text size="md" fw={FONT_WEIGHTS.medium} c="error.7" mt={8}>
            {error.message ?? 'An error occurred'}
          </Text>
        )}
        {extra && <Box mt={16}>{extra}</Box>}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          h={48}
          mt={32}
          onClick={onConfirm}
          loading={isLoading}
          style={{ border: '2px solid #FFFFFF' }}
        >
          {confirmLabel}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          fullWidth
          h={48}
          mt={8}
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelLabel}
        </Button>
      </Stack>
    </Modal>
  );
}
